<script setup>
import { computed } from 'vue';
import { useMapGetter } from 'dashboard/composables/store.js';
import Icon from 'next/icon/Icon.vue';

const props = defineProps({
  to: { type: [Object, String], default: '' },
  label: { type: String, default: '' },
  icon: { type: [String, Object], default: '' },
  expandable: { type: Boolean, default: false },
  isExpanded: { type: Boolean, default: false },
  isActive: { type: Boolean, default: false },
  hasActiveChild: { type: Boolean, default: false },
  getterKeys: { type: Object, default: () => ({}) },
});

const emit = defineEmits(['toggle']);

const showBadge = useMapGetter(props.getterKeys.badge);
const dynamicCount = useMapGetter(props.getterKeys.count);
const count = computed(() =>
  dynamicCount.value > 99 ? '99+' : dynamicCount.value
);
</script>

<template>
  <!-- custom (2026-08-07): a linha de menu desce de 42px/15px para 32px/13px, a escala da
       Attio. A régua que sustenta os números: a caixa é ~2,5× a altura da letra (32/13), que
       é a proporção em que o rótulo respira sem a linha virar um bloco; o raio cai junto
       (12 → 8) porque canto arredondado demais numa caixa baixa vira pastilha; e o gap
       12 → 10 mantém ícone e texto lidos como uma unidade só, e não como duas colunas.
       Ativo = #EEEFF1 (.mj-nav-ativo), o mesmo tom do hover — decisão de sempre. -->

  <!-- ⚠️ 36 → 30px em 2026-08-08, e a MEDIDA veio de pôr as duas barras lado a lado. Com 36
       de caixa e 4 de gap, o passo era 40px contra 30 da referência: a mesma lista ocupava um
       terço a mais de coluna, e ele reagiu duro — "olha como deve ser a sidebar na referência
       e como você deixou a minha, é inadmissível".
       O rótulo também escureceu (slate-11 → slate-12) e perdeu o peso médio: na referência os
       itens são tinta cheia e quem marca o ativo é o FUNDO, não a cor da letra. Texto
       apagado em lista de navegação faz tudo parecer desabilitado.
       O ícone segue um degrau abaixo do rótulo — ver a nota em SidebarGroup. -->

  <!-- custom (redesign Modeloja · F1): top-level nav item anatomy — ink-on-press states
       (docs/plans/_entregue/redesign/F1-shell-redline.md §2.3). -->
  <!-- ⚠️ 34 → 36px em 2026-08-09 (tarde), acompanhando o rótulo que subiu para 17px.
       A regra da casa é a razão caixa/letra ficar perto de 2,3, e ela não é decorativa: é o
       que impede o rótulo de encostar nas bordas da linha. 17px numa caixa de 34 daria 2,0 —
       apertado; com 36 volta a 2,1. Fonte e linha andam juntas nesta barra, sempre. -->
  <!-- ⚠️ 36 → 34px em 2026-08-11, DESCENDO junto com o rótulo (17 → 14px). Fonte e linha
       andam juntas nesta barra nas duas direções — 14px numa caixa de 36 daria 2,57, e a
       linha passaria a ler como vazia em volta da palavra. Com 34 a razão fica em 2,43,
       dentro da faixa de ~2,3 da casa. Ver a nota do rótulo para o motivo da descida. -->
  <component
    :is="to ? 'router-link' : 'div'"
    class="flex items-center gap-2.5 px-3 h-[34px] rounded-lg min-w-0 transition-[background,transform] duration-150 ease-[cubic-bezier(.2,.7,.3,1)] active:scale-[.98] focus-visible:outline-2 focus-visible:outline-n-slate-12 focus-visible:outline-offset-2"
    role="button"
    draggable="false"
    :to="to"
    :title="label"
    :class="{
      'text-n-slate-12 mj-nav-ativo font-semibold': isActive && !hasActiveChild,
      'text-n-slate-12 font-semibold': hasActiveChild,
      'text-n-slate-12 font-normal mj-nav-hover': !isActive && !hasActiveChild,
    }"
    @click.stop="emit('toggle')"
  >
    <!-- ⚠️ A CAIXA fica em 20px (`size-5`) e o DESENHO desce para 18 (2026-08-11).
         A caixa é invariante: é ela que casa com a `leading-5` do rótulo ao lado, e é disso
         que depende o alinhamento (ver a nota do ícone logo abaixo). O que se ajusta é o
         desenho dentro dela. Com o rótulo em 14px, um ícone de 20 ficaria 1,43× a letra e a
         fileira voltaria a ler como uma coluna de símbolos com legenda; 18px dá 1,29×, que é
         a proporção em que o desenho acompanha a palavra em vez de dominá-la.
         Mesmo padrão que o SidebarGroupLeaf já usava: caixa de 20, desenho menor dentro. -->
    <div
      v-if="icon"
      class="relative flex items-center justify-center size-5 shrink-0"
    >
      <!-- custom (2026-08-07): o ícone estava com tinta CHEIA (slate-12) enquanto o rótulo
           do item inativo ficava apagado (slate-11) — o peso invertido em relação à
           referência, onde o desenho é sempre mais claro que a palavra.
           Faz diferença de leitura, não só de gosto: numa coluna de dez linhas, o olho
           percorre os RÓTULOS; o ícone é a marca de reconhecimento que vem depois. Quando
           ele é a coisa mais escura da linha, a lista vira uma fileira de símbolos e o
           texto some. Agora o ícone anda um degrau abaixo do rótulo nos dois estados. -->
      <!-- ⚠️ 18 → 20px, E ISSO CONSERTA O DESALINHAMENTO — não é só "ícone maior".
           O Gustavo pegou que "a palavra Início está um pouco mais acima em relação ao
           ícone", e a causa era a diferença das CAIXAS: o desenho vinha numa de 18px e o
           rótulo numa de 20px (a `leading-5`). `items-center` centraliza as duas, e duas
           caixas de alturas diferentes centralizadas têm centros iguais mas conteúdos que
           não coincidem — o texto sobra 1px para cima.
           Com as duas em 20px o problema deixa de existir por construção. É a MESMA regra
           que o TelaHeader já seguia ("`leading-5` e `size-5` andam juntos"); a barra é que
           tinha ficado de fora dela. Vale para o Ajuda pelo mesmo motivo — ele já estava
           certo, e agora os três concordam. -->
      <Icon
        v-if="icon"
        :icon="icon"
        class="size-[18px] shrink-0"
        :class="
          isActive || hasActiveChild ? 'text-n-slate-12' : 'text-n-slate-10'
        "
      />
      <span
        v-if="showBadge"
        class="size-2 -top-px ltr:-right-px rtl:-left-px bg-n-amber-9 absolute rounded-full border border-n-solid-1"
      />
    </div>
    <div
      class="flex items-center gap-1.5 flex-grow justify-between min-w-0 flex-1"
    >
      <!-- ⚠️ 14 → 15px em 2026-08-09. Pedido do Gustavo comparando com a referência
           (Basepoint): "os nomezinhos estão muito pequenos, estão ocupando pouco espaço".
           MEDI antes de mexer, e o resultado foi contraintuitivo: a referência também usa
           ~14px. O que muda é a JANELA — a dela tem 1206px de largura e a dele tem 1600,
           então o mesmo 14px ocupa proporcionalmente menos coluna aqui e lê como menor.
           Por isso a subida é de UM degrau e não dois: 15px devolve presença sem quebrar a
           proporção com a caixa de 30px (30/15 = 2,0; a régua da casa é ~2,3). 16px exigiria
           subir a linha junto, e ele disse na mesma conversa que o espaçamento está bom.
           O sub-item (SidebarGroupLeaf) sobe junto, um degrau abaixo.

           15 → 16px na mesma sessão, a pedido dele depois de ver na tela: 15 ainda
           parecia pequeno. A LINHA sobe junto (30 → 34px) e isso não é opcional —
           16px numa caixa de 30 dá razão 1,9 e o rótulo encosta nas bordas. Com 34
           a razão volta a 2,1, perto da régua de ~2,3 da casa. Ele tinha dito que o
           espaçamento estava bom, então avisei que subir a fonte obrigava a subir a
           linha, e ele mandou fazer as duas. -->
      <!-- 16 → 17px em 2026-08-09 (tarde): "aumente um pouco a fonte, levemente, porque
           está pequena em relação ao espaço da Sidebar". UM degrau, que é o que "levemente"
           quer dizer — e com a barra em 296px a linha continua cabendo sem truncar.
           `leading-5` é OBRIGATÓRIO aqui e não é estilo: sem ele o `text-sm` do `<aside>`
           mandava na entrelinha, e era essa caixa herdada que brigava com a do ícone. Ver a
           nota do ícone logo acima — as duas linhas resolvem o mesmo defeito e não se
           separam. -->
      <!-- ⚠️ 17 → 14px em 2026-08-11, e esta é a REVERSÃO de uma escada que subiu demais.
           O histórico acima conta a subida degrau por degrau (14 → 15 → 16 → 17) ao longo de
           2026-08-09, cada passo pedido olhando a tela. O destino ficou errado, e o Gustavo
           nomeou o que estava vendo: "tá muito grande, tô sentindo que tá feio, tá amador".
           Ele tem razão, e a régua explica por quê: rótulo de navegação é CHROME — a coisa
           que se lê de relance para se localizar, não o conteúdo. Attio, Linear e Stripe
           ficam todos em 13–14px nessa função, e é justamente por isso que a barra deles
           desaparece atrás do trabalho. Em 17px o menu competia com o conteúdo da tela.
           A queixa da subida original era outra e continua atendida por outro meio: ela era
           "está ocupando pouco espaço" — resolvida agora pela LARGURA da barra (312px), que é
           o lugar certo de dar presença. Aumentar o corpo da letra foi tratar sintoma.
           `leading-5` fica: é a caixa de 20px que casa com a do ícone. Não é estilo. -->
      <span class="truncate text-[14px] leading-5">
        {{ label }}
      </span>
      <!-- custom (F1): yellow count pill — one of the 2-3 sanctioned yellow touches.
           Ink text on brand yellow (#FFD400 = n-amber-9). Inbox unread lives here. -->
      <span
        v-if="dynamicCount && !expandable"
        class="bg-n-amber-9 text-n-slate-12 font-mono text-mj-11 font-600 min-w-[19px] h-[17px] px-1.5 rounded-full inline-flex items-center justify-center flex-shrink-0"
      >
        {{ count }}
      </span>
    </div>
    <span
      v-if="expandable"
      v-show="isExpanded"
      class="i-lucide-chevron-up size-3 text-n-slate-9"
      @click.stop="emit('toggle')"
    />
  </component>
</template>
